import { ReactElement, createRef, useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { CategoryInterface } from '../../../services/exports/Interfaces';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useMenu from '../../../hooks/menu/useMenu';
import useScrollPosition from '../../../hooks/utility/useScrollPosition';
import CloseCircleIcon from '../../../assets/icons/CloseCircleIcon';

interface Props {
  open: boolean;
  categories: CategoryInterface[];
  focusedCategoryId: number;
  setOpen: (open: boolean) => void;
  onSelectCategory: (category: CategoryInterface) => void;
}

const DesktopMenuSideBar = (props: Props): ReactElement => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MenuNavigationBar:DesktopMenuSideBar' });

  const { open, categories, focusedCategoryId, setOpen, onSelectCategory } = props;

  const { selectedMenu } = useMenu();
  const { scrollPosition } = useScrollPosition();

  const openRef = useRef(open);
  openRef.current = open;

  const listItemHeight: number = 50;

  const animationDuration: number = 0.3;

  const itemListRefs = categories?.reduce((acc: any, value: any) => {
    acc[value?.id] = createRef();

    return acc;
  }, {});

  const menuButton = document.getElementById('menu-button');

  useEffect(() => {
    if (open) {
      scrollToElement(focusedCategoryId);
    }
  }, [open]);

  function closeModal(): void {
    setOpen(false);
  }

  function scrollToElement(id: number): void {
    itemListRefs[id]?.current?.scrollIntoView({ block: 'center' });
  }

  /**
   * No AnimatePresence here, deliberately. This component returns null when `open` is false, so
   * the wrapper unmounted along with its child and could never run an exit animation — and
   * neither child below defines `exit` in the first place. What it did do was register a keyless
   * presence child, the shape that crashed React's deletion in LoadingProvider
   * (Sentry FOODAMIGOS-SHOP-WEB-1Z4 / -1ZS). The mount animations are `initial`/`animate` on the
   * motion components themselves and are unaffected. If an exit animation is wanted here, the
   * sidebar needs the two-flag pattern MobileMenuSideBar uses: keep AnimatePresence mounted on
   * `show` and toggle its keyed child on `open`.
   */
  const renderBackgroundOverlay = (): ReactElement => {
    return (
      <motion.div
        className={`fixed inset-0 z-50`}
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        transition={{
          duration: animationDuration,
          ease: 'easeOut',
        }}
        onClick={closeModal}
      />
    );
  };

  const renderCategories = useMemo(
    () =>
      categories &&
      categories?.map((item, index) => {
        const isActive = focusedCategoryId === item?.id;

        return (
          <div
            className={classnames(
              'flex items-center px-md cursor-pointer text-default hover:text-brand-primary relative',
              {
                'text-brand-primary': isActive,
              },
              {
                'border-b': index !== categories?.length - 1,
              },
            )}
            style={{ height: listItemHeight }}
            onClick={() => onSelectCategory(item)}
            ref={itemListRefs[item?.id]}
            key={`menu-sidebar-item-${index}`}
          >
            {isActive && <div className={'w-[5px] h-full bg-brand-primary absolute left-0'} />}
            <h6 className="text-sm">{item?.name}</h6>
          </div>
        );
      }),
    [categories, focusedCategoryId, itemListRefs],
  );

  const renderArrow = useMemo((): ReactElement => {
    const arrowWidth = 14;

    return (
      <div
        className="overflow-hidden absolute top-0 drop-shadow-2xl"
        style={{
          left: -arrowWidth,
          width: arrowWidth,
          zIndex: 60,
        }}
      >
        <div className=" h-[20px] bg-brand-background-secondary -rotate-45 transform origin-top-right -mr-2xs"></div>
      </div>
    );
  }, [scrollPosition]);

  const renderMenuModal = (): ReactElement => {
    const menuButtonRect = menuButton?.getBoundingClientRect();

    return (
      <div
        className="flex relative"
        style={{
          left: menuButtonRect?.left + 50,
          top: menuButtonRect?.top,
        }}
      >
        {renderArrow}
        <motion.div
          className="min-w-[300px] z-50 rounded-lg drop-shadow-2xl overflow-clip"
          initial={{
            x: -10,
            opacity: 0,
          }}
          animate={{
            x: 0,
            opacity: 1,
          }}
          transition={{
            duration: animationDuration,
            ease: 'easeOut',
          }}
        >
          <div className="w-full h-full bg-brand-background-secondary rounded-lg relative overflow-auto">
            <div className="flex items-center justify-between p-md border-b sticky top-0 bg-brand-background-secondary z-10">
              <h6>{selectedMenu?.name ?? t('full_menu')}</h6>
              <button onClick={closeModal}>
                <CloseCircleIcon />
              </button>
            </div>
            {renderCategories}
          </div>
        </motion.div>
      </div>
    );
  };

  return open ? (
    <div className="fixed inset-0" style={{ zIndex: 1000 }}>
      {renderBackgroundOverlay()}
      {renderMenuModal()}
    </div>
  ) : null;
};

export default DesktopMenuSideBar;
